import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, merge, Subject, scan, shareReplay } from 'rxjs';

export enum ActionTypeEnum {
  add = 'add',
  update = 'update',
  delete = 'delete',
  none = 'none',
}

export interface Action<T> {
  item: T;
  action: ActionTypeEnum;
}
interface Identifiable {
  id: number;
}

@Injectable({
  providedIn: 'root',
})
export class CrudService<T extends Identifiable> {
  private itemModifiedSubject = new Subject<Action<T>>();
  itemModifiedAction$ = this.itemModifiedSubject.asObservable();

  private currentEntitySubject = new BehaviorSubject<T>(null);
  currentEntity$ = this.currentEntitySubject.asObservable();

  constructor(private item$: Observable<T[]>) {}
  modifyData(data: T[], operation: Action<T>): T[] {
    const item = operation.item;
    const action = operation.action;
    if (action === ActionTypeEnum.add) {
      return [...data, item];
    }
    if (action === ActionTypeEnum.update) {
      const index = data.findIndex((d: T) => d.id === item.id);
      data[index] = item;
      return [...data];
    }
    if (action === ActionTypeEnum.delete) {
      const index = data.findIndex((d: T) => d.id === item.id);
      data.splice(index, 1);
      return [...data];
    }
    return [...data];
  }

  itemsWithCRUD$: Observable<T[]> = merge(
    this.item$,
    this.itemModifiedAction$,
  ).pipe(
    scan((acc: T[], curr: T[] | Action<T>) => {
      if (Array.isArray(curr)) return curr;
      return [...(this.modifyData(acc, curr) as T[])];
    }, [] as T[]),

    shareReplay(1),
  );

  addItem(item: T): void {
    const action: Action<T> = { item, action: ActionTypeEnum.add };
    return this.itemModifiedSubject.next(action);
  }

  updateItem(item: T): void {
    this.itemModifiedSubject.next({
      item,
      action: ActionTypeEnum.update,
    });
  }

  deleteItem(item: T): void {
    this.itemModifiedSubject.next({
      item,
      action: ActionTypeEnum.delete,
    });
  }
  setCurrentEntity(item: T): void {
    this.currentEntitySubject.next(item);
  }
  getCurrentEntity(): Observable<T> {
    return this.currentEntity$;
  }
  getCurrentEntitySubject(): BehaviorSubject<T> {
    return this.currentEntitySubject;
  }
}
